import React from "react";
import { Document, Page, View, Text, Image as PdfImage, StyleSheet, Svg, Path } from "@react-pdf/renderer";
import { MM_TO_PT } from "../lib/printer";

export const PEPPER_BODY = "M5.5 9.5 C4 14 6.5 20 14 21.6 C17.5 22.3 20 21.4 19.4 20.3 C14 18.6 11.6 14.2 11.8 9.5 Z";
export const PEPPER_CAP = "M4.8 10 C5.8 7.6 11 7.4 12.4 9.8 Z";
export const PEPPER_STEM = "M8.6 8.2 C8.4 5.4 10 3.4 12.6 2.6";

// Niveau d'épice : produit en priorité, sinon niveau menu. Borné 0..3.
export const getSpiceLevel = (p) => {
  const lvl = Number(p?.spice_level) > 0 ? Number(p.spice_level) : Number(p?.menu_spice_level) || 0;
  return Math.max(0, Math.min(3, Math.round(lvl)));
};

const PdfPeppers = ({ level, size = 13 }) => (
  <View style={{ flexDirection: "row", marginLeft: 3, marginTop: 4 }}>
    {Array.from({ length: level }, (_, i) => (
      <Svg key={i} viewBox="0 0 24 24" style={{ width: size, height: size, marginLeft: i ? -1 : 0 }}>
        <Path d={PEPPER_BODY} fill="#D0021B" />
        <Path d={PEPPER_CAP} fill="#2E7D32" />
        <Path d={PEPPER_STEM} stroke="#2E7D32" strokeWidth={1.8} fill="none" strokeLinecap="round" />
      </Svg>
    ))}
  </View>
);

const TitleRow = ({ l, style }) => (
  <View style={{ flexDirection: "row", alignItems: "flex-start" }}>
    <Text style={[style, { flexShrink: 1 }]}>{l.name}</Text>
    {l.spice_level > 0 ? <PdfPeppers level={l.spice_level} /> : null}
  </View>
);

/**
 * Génération PDF des étiquettes Epson C3500 (51mm) + variante A4.
 * Extrait de Labels.jsx — iter22 refactor.
 *
 * Props:
 * - labels: array d'objets { name, sku, lot_number, production_date, dlc, allergens,
 *   ingredients, nutrition, net_weight, net_weight_unit, net_weight_detail,
 *   storage_instructions, reheating_instructions, qrDataUrl, barDataUrl }
 * - format: { id, width, height } depuis LABEL_FORMATS
 * - company, companyAddress, companyLogo: en-tête entreprise
 * - legalMentions: mentions légales (Fabrication artisanale, MAP, …)
 * - nutritionMode: "inline" (intégré) | "separate" (étiquette nutritionnelle séparée) | "none"
 */
const s = StyleSheet.create({
  page: { paddingTop: 6, paddingBottom: 6, paddingLeft: 13, paddingRight: 10, fontFamily: "Helvetica" },
  brand: { fontSize: 11, color: "#1A1C1A", letterSpacing: 0.8, textTransform: "uppercase", fontFamily: "Helvetica-Bold" },
  brandRow: { flexDirection: "row", alignItems: "center", gap: 6 },
  logo: { width: 22, height: 22, objectFit: "contain" },
  address: { fontSize: 7, color: "#1A1C1A", marginTop: 1, lineHeight: 1.25 },
  title: { fontSize: 19, fontWeight: 700, color: "#8B1A1A", marginTop: 4, lineHeight: 1.1, fontFamily: "Helvetica-Bold" },
  sku: { fontSize: 6, color: "#A3A6A3" },
  lotLabel: { fontSize: 6, color: "#575A57", textTransform: "uppercase", letterSpacing: 0.5 },
  lotValue: { fontSize: 12, color: "#1A1C1A", fontWeight: 700, fontFamily: "Helvetica-Bold" },
  row: { flexDirection: "row", justifyContent: "space-between", marginTop: 4 },
  meta: { fontSize: 8, color: "#1A1C1A", fontFamily: "Helvetica-Bold" },
  metaLabel: { color: "#575A57", fontSize: 6, textTransform: "uppercase", letterSpacing: 0.5 },
  allergens: { marginTop: 3, fontSize: 7, color: "#8C6B2F" },
  allergensLabel: { fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: "Helvetica-Bold" },
  allergensValue: { fontWeight: 700, fontFamily: "Helvetica-Bold" },
  ingredients: { marginTop: 3, fontSize: 6.5, color: "#1A1C1A", lineHeight: 1.3 },
  ingredientsLabel: { fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: "Helvetica-Bold" },
  netWeight: { fontSize: 10, fontWeight: 700, color: "#1A1C1A", marginTop: 2, fontFamily: "Helvetica-Bold" },
  storage: { marginTop: 2, fontSize: 7, color: "#1A1C1A", lineHeight: 1.3 },
  storageLabel: { fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#1F4E3D", fontFamily: "Helvetica-Bold" },
  reheating: { marginTop: 1.5, fontSize: 7, color: "#1A1C1A", lineHeight: 1.3 },
  reheatingLabel: { fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#1F4E3D", fontFamily: "Helvetica-Bold" },
  legalMention: { fontSize: 6.5, color: "#575A57", fontFamily: "Helvetica-Oblique", lineHeight: 1.3 },
  nutritionBox: { marginTop: 3, padding: 3, borderWidth: 0.5, borderColor: "#1A1C1A" },
  nutritionTitle: { fontSize: 6, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2, color: "#1A1C1A", fontFamily: "Helvetica-Bold" },
  nutritionRow: { flexDirection: "row", justifyContent: "space-between", fontSize: 6, color: "#1A1C1A" },
  nutritionLabel: { color: "#3A3A3A" },
  nutritionValue: { fontWeight: 700, fontFamily: "Helvetica-Bold" },
  // Étiquette nutrition séparée (plus aéré)
  nutritionPage: { padding: 8, fontFamily: "Helvetica" },
  nutritionPageTitle: { fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#1A1C1A", marginBottom: 2 },
  nutritionPageProduct: { fontSize: 11, fontWeight: 700, color: "#1A1C1A", marginBottom: 2 },
  nutritionPageMeta: { fontSize: 6, color: "#575A57", marginBottom: 6 },
  nutritionPagePer: { fontSize: 7, color: "#3A3A3A", marginBottom: 4, fontFamily: "Helvetica-Oblique" },
  nutritionPageRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 2.5, borderBottom: "0.4pt solid #C5C5C0" },
  nutritionPageLabel: { fontSize: 8, color: "#1A1C1A" },
  nutritionPageValue: { fontSize: 8, fontWeight: 700, color: "#1A1C1A" },
  nutritionPageFooter: { fontSize: 5.5, color: "#575A57", marginTop: 6, textAlign: "center" },
  sep: { borderTop: "0.5pt solid #1A1C1A", marginVertical: 4 },
  codes: { flexDirection: "column", alignItems: "center", marginTop: 6 },
  imgQr: { width: 70, height: 70, marginBottom: 5 },
  imgBar: { width: 130, height: 32 },
  // Variantes A4
  a4Title: { fontSize: 16, fontWeight: 700 },
  a4Label: { border: "1pt solid #1A1C1A", padding: 14, marginBottom: 12, borderRadius: 4 },
});

// Export du StyleSheet pour réutilisation éventuelle (LabelPreview, etc.)
export const labelStyles = s;

export default function LabelPdf({ labels, format, company, companyAddress, companyLogo, legalMentions = "", nutritionMode = "inline" }) {
  const isA4 = format.id === "A4";
  const size = isA4 ? "A4" : [format.width * MM_TO_PT, format.height * MM_TO_PT];
  const showInlineNutrition = nutritionMode === "inline";
  const showSeparateNutrition = nutritionMode === "separate";
  // Fallback : si aucun logo n'est configuré dans Paramètres, utilise le logo
  // Maison Thaï servi statiquement par le frontend. URL absolue obligatoire
  // pour que @react-pdf puisse fetcher l'image.
  const effectiveLogo = companyLogo || (typeof window !== "undefined" ? `${window.location.origin}/logo-maisonthai-pdf.png` : null);

  if (isA4) {
    return (
      <Document>
        <Page size="A4" style={{ padding: 28, fontFamily: "Helvetica" }}>
          {labels.map((l, i) => (
            <View key={i} style={s.a4Label} wrap={false}>
              <View style={s.row}>
                <View style={{ flexDirection: "row", alignItems: "center", gap: 6, flex: 1 }}>
                  {effectiveLogo ? <PdfImage src={effectiveLogo} style={{ width: 28, height: 28, objectFit: "contain" }} /> : null}
                  <View>
                    <Text style={s.brand}>{company}</Text>
                    {companyAddress ? <Text style={s.address}>{companyAddress}</Text> : null}
                    <TitleRow l={l} style={s.a4Title} />
                    <Text style={s.sku}>SKU: {l.sku}</Text>
                    {l.net_weight != null && (
                      <Text style={s.netWeight}>
                        Poids net : {l.net_weight} {l.net_weight_unit}
                        {l.net_weight_detail ? ` (${l.net_weight_detail})` : ""}
                      </Text>
                    )}
                    {l.allergens?.length > 0 && (
                      <Text style={s.allergens}>
                        <Text style={s.allergensLabel}>Allergènes : </Text>
                        <Text style={s.allergensValue}>{l.allergens.join(", ")}</Text>
                      </Text>
                    )}
                    {l.ingredients ? (
                      <Text style={s.ingredients}>
                        <Text style={s.ingredientsLabel}>Ingrédients : </Text>
                        {l.ingredients}
                      </Text>
                    ) : null}
                    {l.nutrition?.length > 0 && (
                      <View style={s.nutritionBox}>
                        <Text style={s.nutritionTitle}>Valeurs nutritionnelles · pour 100 g</Text>
                        {l.nutrition.map((row, idx) => (
                          <View key={idx} style={s.nutritionRow}>
                            <Text style={s.nutritionLabel}>{row.label}</Text>
                            <Text style={s.nutritionValue}>{row.value}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                    {l.storage_instructions ? (
                      <Text style={s.storage}>
                        <Text style={s.storageLabel}>Conservation : </Text>
                        {l.storage_instructions}
                      </Text>
                    ) : null}
                    {l.reheating_instructions ? (
                      <Text style={s.reheating}>
                        <Text style={s.reheatingLabel}>Réchauffage : </Text>
                        {l.reheating_instructions}
                      </Text>
                    ) : null}
                  </View>
                </View>
                <View>
                  <Text style={s.metaLabel}>Lot</Text>
                  <Text style={{ fontSize: 12, fontWeight: 700 }}>{l.lot_number}</Text>
                </View>
              </View>
              <View style={s.row}>
                <View><Text style={s.metaLabel}>Prod.</Text><Text style={s.meta}>{l.production_date || "—"}</Text></View>
                <View><Text style={s.metaLabel}>DLC</Text><Text style={s.meta}>{l.dlc || "—"}</Text></View>
              </View>
              <View style={{ flexDirection: "row", alignItems: "center", marginTop: 10, justifyContent: "space-between" }}>
                {l.qrDataUrl && <PdfImage src={l.qrDataUrl} style={{ width: 56, height: 56 }} cache={false} />}
                {l.barDataUrl && <PdfImage src={l.barDataUrl} style={{ width: 160, height: 34 }} cache={false} />}
              </View>
            </View>
          ))}
        </Page>
      </Document>
    );
  }

  // Layout étroit (Epson C3500 51mm)
  return (
    <Document>
      {labels.map((l, i) => (
        <Page key={i} size={size} style={s.page}>
          <View style={s.brandRow}>
            {effectiveLogo ? <PdfImage src={effectiveLogo} style={s.logo} /> : null}
            <View style={{ flex: 1 }}>
              <Text style={s.brand}>{company}</Text>
              {companyAddress ? <Text style={s.address}>{companyAddress}</Text> : null}
            </View>
          </View>
          <TitleRow l={l} style={s.title} />
          <Text style={s.sku}>SKU: {l.sku}</Text>
          {l.net_weight != null && (
            <Text style={s.netWeight}>
              Poids net : {l.net_weight} {l.net_weight_unit}
              {l.net_weight_detail ? ` (${l.net_weight_detail})` : ""}
            </Text>
          )}
          {l.allergens?.length > 0 && (
            <Text style={s.allergens}>
              <Text style={s.allergensLabel}>Allergènes : </Text>
              <Text style={s.allergensValue}>{l.allergens.join(", ")}</Text>
            </Text>
          )}
          {l.ingredients ? (
            <Text style={s.ingredients}>
              <Text style={s.ingredientsLabel}>Ingrédients : </Text>
              {l.ingredients}
            </Text>
          ) : null}
          {showInlineNutrition && l.nutrition?.length > 0 && (
            <View style={s.nutritionBox}>
              <Text style={s.nutritionTitle}>Valeurs nutritionnelles · pour 100 g</Text>
              {l.nutrition.map((row, idx) => (
                <View key={idx} style={s.nutritionRow}>
                  <Text style={s.nutritionLabel}>{row.label}</Text>
                  <Text style={s.nutritionValue}>{row.value}</Text>
                </View>
              ))}
            </View>
          )}
          {l.storage_instructions ? (
            <Text style={s.storage}>
              <Text style={s.storageLabel}>Conservation : </Text>
              {l.storage_instructions}
            </Text>
          ) : null}
          {l.reheating_instructions ? (
            <Text style={s.reheating}>
              <Text style={s.reheatingLabel}>Réchauffage : </Text>
              {l.reheating_instructions}
            </Text>
          ) : null}
          {legalMentions ? (
            <View style={{ marginTop: 4 }}>
              {legalMentions.split("\n").filter(Boolean).map((line, idx) => (
                <Text key={idx} style={s.legalMention}>{line}</Text>
              ))}
            </View>
          ) : null}
          <View style={s.sep} />
          <View style={{ flexDirection: "row", alignItems: "baseline", gap: 4 }}>
            <Text style={s.lotLabel}>Lot :</Text>
            <Text style={s.lotValue}>{l.lot_number}</Text>
          </View>
          <View style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 4 }}>
            <View>
              <Text style={s.metaLabel}>Prod.</Text>
              <Text style={s.meta}>{l.production_date || "—"}</Text>
            </View>
            <View>
              <Text style={s.metaLabel}>DLC</Text>
              <Text style={s.meta}>{l.dlc || "—"}</Text>
            </View>
          </View>
          <View style={s.codes}>
            {/* Phase 101gc.3 — cache=false : sans ça, @react-pdf déduplique
                les images (même dataURL) et n'affiche le QR/barcode QUE sur
                la première étiquette d'un batch multi-copies. */}
            {l.qrDataUrl && <PdfImage src={l.qrDataUrl} style={s.imgQr} cache={false} />}
            {l.barDataUrl && <PdfImage src={l.barDataUrl} style={s.imgBar} cache={false} />}
          </View>
        </Page>
      ))}
      {/* Étiquettes nutritionnelles séparées (une par étiquette principale) */}
      {showSeparateNutrition && labels.map((l, i) => (
        l.nutrition?.length > 0 ? (
          <Page key={`nut-${i}`} size={size} style={s.nutritionPage}>
            <View style={s.brandRow}>
              {effectiveLogo ? <PdfImage src={effectiveLogo} style={s.logo} /> : null}
              <View style={{ flex: 1 }}>
                <Text style={s.brand}>{company}</Text>
                {companyAddress ? <Text style={s.address}>{companyAddress}</Text> : null}
              </View>
            </View>
            <Text style={s.nutritionPageTitle}>Valeurs nutritionnelles</Text>
            <Text style={s.nutritionPageProduct}>{l.name}</Text>
            <Text style={s.nutritionPageMeta}>
              Lot : {l.lot_number || "—"}
              {l.dlc ? `  ·  DLC : ${l.dlc}` : ""}
            </Text>
            <Text style={s.nutritionPagePer}>Pour 100 g</Text>
            {l.nutrition.map((row, idx) => (
              <View key={idx} style={s.nutritionPageRow}>
                <Text style={s.nutritionPageLabel}>{row.label}</Text>
                <Text style={s.nutritionPageValue}>{row.value}</Text>
              </View>
            ))}
            {l.allergens?.length > 0 && (
              <Text style={[s.allergens, { marginTop: 6 }]}>
                <Text style={s.allergensLabel}>Allergènes : </Text>
                <Text style={s.allergensValue}>{l.allergens.join(", ")}</Text>
              </Text>
            )}
            <Text style={s.nutritionPageFooter}>Conformité INCO · Règlement (UE) 1169/2011</Text>
          </Page>
        ) : null
      ))}
    </Document>
  );
}
